import { Injectable } from '@angular/core';
import { bbox as turfBbox } from '@turf/turf';
import type { BBox, FeatureCollection as GeoJsonFeatureCollection } from 'geojson';

import { Feature, FeatureCollection } from '@connect-services/src/sdk/api';

export enum StoreState {
    FETCHING = 'FETCHING',
    LOADED = 'LOADED',
    NOT_FOUND = 'NOT_FOUND',
}

export type GeohashFetching = {
    geojson: undefined;
    state: StoreState.FETCHING;
};

export type GeohashLoaded = {
    geojson: FeatureCollection;
    state: StoreState.LOADED;
};

export type GeohashNotFound = {
    geojson: null;
    state: StoreState.NOT_FOUND;
};

export type GeohashStorage = {
    [key: string]: GeohashFetching | GeohashLoaded | GeohashNotFound;
};

export type LayerStorage = {
    [key: string]: GeohashStorage;
};

@Injectable({
    providedIn: 'root',
})
export class MapLayerStorageService {
    private store: LayerStorage = {};

    public getGeojson(layerId: string, geohashes: string[]): FeatureCollection {
        const result = geohashes.reduce((features: Feature[], geohash: string) => {
            if (!this.getLayerGeohashes(layerId) || this.store[layerId][geohash]?.state !== StoreState.LOADED) {
                return features;
            }

            // eslint-disable-next-line @typescript-eslint/no-non-null-assertion
            return [...features, ...this.getGeojsonForlayer(layerId, geohash)!.features];
        }, []);

        let bbox: BBox | undefined;
        if (result.length > 0) {
            bbox = turfBbox(
                {
                    features: result,
                    type: FeatureCollection.TypeEnum.FEATURE_COLLECTION,
                } as unknown as GeoJsonFeatureCollection,
                { recompute: true },
            );
        }

        return {
            bbox: bbox,
            features: result,
            type: FeatureCollection.TypeEnum.FEATURE_COLLECTION,
        };
    }

    public getGeojsonForlayer(layerId: string, geohash: string): FeatureCollection | null | undefined {
        return this.store[layerId][geohash].geojson;
    }

    public getLayerGeohashes(layerId: string): GeohashStorage | undefined {
        return this.store[layerId];
    }

    public isGeohashFetching(layerId: string, geohash: string): boolean {
        return this.store[layerId]?.[geohash]?.state === StoreState.FETCHING;
    }

    public resetLayerGeohash(layerIds: string[]): void {
        layerIds.forEach((layerId) => {
            delete this.store[layerId];
        });
    }

    public setGeohashFetching(layerId: string, geohash: string): void {
        if (!this.store[layerId]) {
            this.store[layerId] = {};
        }
        this.store[layerId][geohash] = { geojson: undefined, state: StoreState.FETCHING };
    }

    public storeGeohashForLayer(layerId: string, geohash: string, value: FeatureCollection | null): void {
        if (!this.store[layerId]) {
            this.store[layerId] = {};
        }

        if (!value) {
            this.store[layerId][geohash] = { geojson: value, state: StoreState.NOT_FOUND };

            return;
        }

        this.store[layerId][geohash] = { geojson: value, state: StoreState.LOADED };
    }
}
